<template>
  <form @submit.prevent="onSubmit" class="pickup-request-form">
    <AppField label="Fecha" :invalid-feedback="errors.date" :state="errors.date ? false : null" required>
      <AppInput v-model="form.date" placeholder="Fecha" type="date"
        @blur="validateField('date', form.date, validationRules.date)"
        @update:modelValue="validateField('date', form.date, validationRules.date)" />
    </AppField>

    <AppField label="Tienda" :invalid-feedback="errors.storeId" :state="errors.storeId ? false : null" required>
      <AppAutocomplete :items="storesOptions" v-model="form.storeId"
        @update:modelValue="validateField('storeId', form.storeId, validationRules.storeId)" />
    </AppField>

    <AppField label="Encargado de Retiro" :invalid-feedback="errors.finalCustomer"
      :state="errors.finalCustomer ? false : null" required>
      <AppInput v-model="form.finalCustomer"
        @blur="validateField('finalCustomer', form.finalCustomer, validationRules.finalCustomer)"
        @update:modelValue="validateField('finalCustomer', form.finalCustomer, validationRules.finalCustomer)" />
    </AppField>

    <AppField label="Teléfono" :invalid-feedback="errors.contactPhone" :state="errors.contactPhone ? false : null"
      required>
      <AppInput v-model="form.contactPhone" placeholder="Ej: 912345678"
        @blur="validateField('contactPhone', form.contactPhone, validationRules.contactPhone)"
        @update:modelValue="validateField('contactPhone', form.contactPhone, validationRules.contactPhone)" />
    </AppField>

    <AppField label="Dirección de Retiro" :invalid-feedback="errors.address" :state="errors.address ? false : null"
      required>
      <AppInput v-model="form.address" @blur="validateField('address', form.address, validationRules.address)"
        @update:modelValue="validateField('address', form.address, validationRules.address)" />
    </AppField>

    <AppField label="Comuna" :invalid-feedback="errors.withdrawalCommuneId"
      :state="errors.withdrawalCommuneId ? false : null" required>
      <AppAutocomplete :items="communesOptions" v-model="form.withdrawalCommuneId"
        @update:modelValue="validateField('withdrawalCommuneId', form.withdrawalCommuneId, validationRules.withdrawalCommuneId)" />
    </AppField>

    <AppField label="Conductor Asignado">
      <AppAutocomplete :items="driversOptions" v-model="form.driverId" />
    </AppField>

    <AppField label="Comentario Interno">
      <AppTextarea v-model="form.internalComment" rows="3" placeholder="Comentarios internos..." />
    </AppField>

    <AppField label="Observaciones">
      <AppTextarea v-model="form.observations" rows="3" placeholder="Instrucciones especiales para el retiro..." />
    </AppField>

    <div class="text-right mt-3" style="display: flex; gap: 10px; justify-content: flex-end;">
      <AppButton type="submit" :disabled="loading" class="app-form-btn">
        Crear
      </AppButton>
    </div>
  </form>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import store from '@/store';

import AppField from '../UI/AppField.vue'
import AppInput from '../UI/AppInput.vue'
import AppTextarea from '../UI/AppTextarea.vue'
import AppButton from '../UI/AppButton.vue'
import AppAutocomplete from '../UI/AppAutocomplete.vue'

import { useValidation } from '@/composables/useValidation';
import { required } from '@/utils/validators';

const defaultObservations = 'PISTOLEAR EN TIENDA (CANTIDAD POR CONFIRMAR)'

const { errors, validateField, validateForm } = useValidation();

const validationRules = {
  date: [required('Fecha es requerida')],
  storeId: [required('Tienda es requerida')],
  finalCustomer: [required('Nombre del destinatario es requerido')],
  contactPhone: [required('Teléfono es requerido')],
  address: [required('Dirección es requerida')],
  withdrawalCommuneId: [required('Comuna es requerida')],
}

const props = defineProps({
  pickupRequest: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['submit'])

const loading = ref(false)

const defaultDate = new Date().toISOString().split('T')[0]

const form = ref({
  date: props.pickupRequest.date || defaultDate,
  storeId: props.pickupRequest.storeId || null,
  customerName: props.pickupRequest.customerName || '',
  finalCustomer: props.pickupRequest.finalCustomer || '',
  contactPhone: props.pickupRequest.contactPhone || '',
  address: props.pickupRequest.address || '',
  withdrawalCommuneId: props.pickupRequest.withdrawalCommuneId || null,
  bundleQuantity: props.pickupRequest.bundleQuantity || 1,
  observations: props.pickupRequest.observations || defaultObservations,
  status: props.pickupRequest.status || 'pending',
  isUrgent: props.pickupRequest.isUrgent || false,
  driverId: props.pickupRequest.driverId || null,
  internalComment: props.pickupRequest.internalComment || '',
})


const stores = ref([])
const storesOptions = computed(() => {
  return stores.value.map(store => ({
    value: store.id,
    text: `${store.name} - ${store.internalCode}`
  }))
})

const communes = ref([])
const communesOptions = computed(() => {
  return communes.value.map(commune => ({
    value: commune.id,
    text: commune.name
  }))
})

const fetchCommunes = async () => {
  const response = await store.dispatch('listAllCommunes')
  communes.value = response
}

const fetchStores = async () => {
  const response = await store.dispatch('listAllStoreName')
  stores.value = response
}

const drivers = ref([])
const driversOptions = computed(() => {
  return drivers.value.map(driver => ({
    value: driver.id,
    text: `${driver.name} ${driver.lastName || ''}`.trim()
  }))
})

const fetchDrivers = async () => {
  const response = await store.dispatch('getAllDrivers')
  drivers.value = response || []
}

const onSubmit = async () => {
  const isValid = validateForm(form.value, validationRules)
  if (!isValid) {
    return
  }

  loading.value = true
  try {
    // Emit payload for parent to handle API actions
    emit('submit', {
      id: props.pickupRequest.id,
      ...form.value
    })
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchStores()
  fetchDrivers()
  fetchCommunes()
})

watch(() => form.value.storeId, (newVal) => {
  console.log(newVal)
  if (newVal) {
    const store = stores.value.find(store => store.id === newVal)
    if (store) {
      form.value.customerName = store.name
      form.value.finalCustomer = store.withdrawalAdgent
      form.value.address = store.withdrawalAddress
      form.value.withdrawalCommuneId = store.withdrawalCommuneId
      form.value.contactPhone = store.pickupPhone1
      form.value.observations = store.pickupObservations || defaultObservations
    }
  } else {
    form.value.customerName = ''
    form.value.finalCustomer = ''
    form.value.address = ''
    form.value.withdrawalCommuneId = null
    form.value.contactPhone = ''
    form.value.observations = defaultObservations
  }
})

const getCommuneName = (id) => {
  const commune = communes.value.find(c => c.id === id)
  return commune ? commune.name : ''
}

watch(() => form.value.withdrawalCommuneId, (newVal) => {
  form.value.commune = newVal ? getCommuneName(newVal) : ''
})

watch(() => form.value, (newVal) => {
  validateForm(newVal, validationRules)
}, { deep: true })
</script>

<style scoped></style>